/**
 * SECURITY: Client-side cryptographic utilities for sensitive data handling
 * This provides basic protection for credentials in transit within the browser
 */

/**
 * Generate a simple hash of the password for additional security
 * NOTE: This is not a replacement for proper HTTPS encryption
 */
export const hashPassword = async (password) => {
  // Use the Web Crypto API for hashing (available in modern browsers)
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(password);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      console.warn('Web Crypto API not available, using fallback:', error);
    }
  }
  
  // Fallback: Simple client-side obfuscation (NOT secure, just obscures)
  return btoa(password + 'salt_skipper_2024').replace(/[+/=]/g, '');
};

/**
 * Securely clear sensitive data from memory
 */
export const clearSensitiveData = (obj) => {
  if (typeof obj === 'string') {
    // For strings, we can't actually clear memory, but we can reassign
    obj = null;
    return;
  }
  
  if (obj && typeof obj === 'object') {
    Object.keys(obj).forEach(key => {
      if (typeof obj[key] === 'string') {
        obj[key] = '';
      }
      obj[key] = null;
    });
  }
};

/**
 * Check if we're running on HTTPS (secure connection)
 */
export const isSecureConnection = () => {
  return window.location.protocol === 'https:' || 
         window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1';
};

/**
 * Warn if connection is not secure in production
 */
export const validateSecureConnection = () => {
  // Only enforce HTTPS for explicit production runtime, not staging/homolog over HTTP.
  // NODE_ENV is always "production" in optimized frontend builds, so it cannot be used alone.
  const appEnv = (process.env.REACT_APP_ENVIRONMENT || '').toLowerCase();
  const enforceHttps = appEnv === 'production' || appEnv === 'prod';

  if (enforceHttps && !isSecureConnection()) {
    console.error('SECURITY WARNING: Authentication over insecure connection detected!');
    throw new Error('Secure connection required for authentication');
  }
};
